import { IProjectTemplate, ITemplateFile } from '@/types';

/**
 * 템플릿 유틸리티 클래스
 */
export class TemplateUtils {
  /**
   * React 템플릿 반환
   */
  public static getReactTemplate(projectName: string): IProjectTemplate {
    return {
      name: 'react',
      description: 'React + TypeScript + Vite 템플릿',
      files: [
        {
          path: 'package.json',
          content: this._generateReactPackageJson(projectName),
        },
        {
          path: 'index.html',
          content: this._generateReactIndexHtml(projectName),
        },
        {
          path: 'src/main.tsx',
          content: this._generateReactMainTsx(),
        },
        {
          path: 'src/App.tsx',
          content: this._generateReactAppTsx(),
        },
        {
          path: 'src/vite-env.d.ts',
          content: this._generateViteEnvDts(),
        },
        {
          path: 'vite.config.ts',
          content: this._generateViteConfig(),
        },
        {
          path: 'tsconfig.json',
          content: this._generateReactTsConfig(),
        },
      ],
      dependencies: [
        'react',
        'react-dom',
        'debug-time-machine-core',
        'debug-time-machine-ui',
      ],
      devDependencies: [
        '@types/react',
        '@types/react-dom',
        '@vitejs/plugin-react',
        'typescript',
        'vite',
      ],
      scripts: {
        dev: 'vite',
        build: 'tsc && vite build',
        preview: 'vite preview',
        lint: 'eslint . --ext ts,tsx --fix',
        'type-check': 'tsc --noEmit',
      },
    };
  }

  /**
   * Node.js 템플릿 반환
   */
  public static getNodeTemplate(projectName: string): IProjectTemplate {
    return {
      name: 'node',
      description: 'Node.js + TypeScript 템플릿',
      files: [
        {
          path: 'package.json',
          content: this._generateNodePackageJson(projectName),
        },
        {
          path: 'src/index.ts',
          content: this._generateNodeIndexTs(),
        },
        {
          path: 'tsconfig.json',
          content: this._generateNodeTsConfig(),
        },
      ],
      dependencies: ['debug-time-machine-core'],
      devDependencies: ['@types/node', 'typescript', 'ts-node', 'nodemon'],
      scripts: {
        start: 'node dist/index.js',
        dev: 'nodemon src/index.ts',
        build: 'tsc',
        'type-check': 'tsc --noEmit',
      },
    };
  }

  /**
   * Express 템플릿 반환
   */
  public static getExpressTemplate(projectName: string): IProjectTemplate {
    return {
      name: 'express',
      description: 'Express.js + TypeScript 템플릿',
      files: [
        {
          path: 'package.json',
          content: this._generateExpressPackageJson(projectName),
        },
        {
          path: 'src/index.ts',
          content: this._generateExpressIndexTs(),
        },
        {
          path: 'src/server.ts',
          content: this._generateExpressServerTs(),
        },
        {
          path: 'tsconfig.json',
          content: this._generateNodeTsConfig(),
        },
      ],
      dependencies: ['express', 'ws', 'debug-time-machine-core'],
      devDependencies: [
        '@types/node',
        '@types/express',
        '@types/ws',
        'typescript',
        'ts-node',
        'nodemon',
      ],
      scripts: {
        start: 'node dist/index.js',
        dev: 'nodemon src/index.ts',
        build: 'tsc',
        'type-check': 'tsc --noEmit',
      },
    };
  }

  // Private template generators
  private static _generateReactPackageJson(projectName: string): string {
    return JSON.stringify(
      {
        name: projectName,
        private: true,
        version: '0.0.0',
        type: 'module',
        scripts: {},
        dependencies: {},
        devDependencies: {},
      },
      null,
      2
    );
  }

  private static _generateReactIndexHtml(projectName: string): string {
    return `<!doctype html>
<html lang="ko">
  <head>
    <meta charset="UTF-8" />
    <link rel="icon" type="image/svg+xml" href="/vite.svg" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>${projectName}</title>
  </head>
  <body>
    <div id="root"></div>
    <script type="module" src="/src/main.tsx"></script>
  </body>
</html>`;
  }

  private static _generateReactMainTsx(): string {
    return `import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App.tsx';
import './index.css';

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);`;
  }

  private static _generateReactAppTsx(): string {
    return `import React from 'react';
import { TimeTravelEngine } from 'debug-time-machine-core';

const timeTravelEngine = new TimeTravelEngine();

function App(): JSX.Element {
  const [count, setCount] = React.useState(0);

  const handleIncrement = (): void => {
    const newCount = count + 1;
    setCount(newCount);
    
    // Create snapshot for debugging
    timeTravelEngine.createSnapshot(
      { count: newCount },
      'INCREMENT_COUNT'
    );
  };

  return (
    <div className="App">
      <h1>Debug Time Machine React App</h1>
      <div>
        <button onClick={handleIncrement}>
          count is {count}
        </button>
      </div>
    </div>
  );
}

export default App;`;
  }

  private static _generateViteEnvDts(): string {
    return `/// <reference types="vite/client" />`;
  }

  private static _generateViteConfig(): string {
    return `import { defineConfig } from 'vite';
import react from '@vitejs/plugin-react';

export default defineConfig({
  plugins: [react()],
});`;
  }

  private static _generateReactTsConfig(): string {
    return JSON.stringify(
      {
        compilerOptions: {
          target: 'ES2020',
          useDefineForClassFields: true,
          lib: ['ES2020', 'DOM', 'DOM.Iterable'],
          module: 'ESNext',
          skipLibCheck: true,
          moduleResolution: 'bundler',
          allowImportingTsExtensions: true,
          resolveJsonModule: true,
          isolatedModules: true,
          noEmit: true,
          jsx: 'react-jsx',
          strict: true,
          noUnusedLocals: true,
          noUnusedParameters: true,
          noFallthroughCasesInSwitch: true,
        },
        include: ['src'],
        references: [{ path: './tsconfig.node.json' }],
      },
      null,
      2
    );
  }

  private static _generateNodePackageJson(projectName: string): string {
    return JSON.stringify(
      {
        name: projectName,
        version: '1.0.0',
        description: '',
        main: 'dist/index.js',
        scripts: {},
        dependencies: {},
        devDependencies: {},
      },
      null,
      2
    );
  }

  private static _generateNodeIndexTs(): string {
    return `import { TimeTravelEngine } from 'debug-time-machine-core';

const timeTravelEngine = new TimeTravelEngine();

console.log('Debug Time Machine Node.js App Started');

// Example state
let appState = {
  counter: 0,
  message: 'Hello World',
};

// Create initial snapshot
timeTravelEngine.createSnapshot(appState, 'INITIAL_STATE');

// Simulate state changes
setInterval(() => {
  appState.counter++;
  appState.message = \`Counter: \${appState.counter}\`;
  
  timeTravelEngine.createSnapshot(appState, 'UPDATE_COUNTER');
  console.log('State updated:', appState);
}, 2000);`;
  }

  private static _generateExpressPackageJson(projectName: string): string {
    return JSON.stringify(
      {
        name: projectName,
        version: '1.0.0',
        description: '',
        main: 'dist/index.js',
        scripts: {},
        dependencies: {},
        devDependencies: {},
      },
      null,
      2
    );
  }

  private static _generateExpressIndexTs(): string {
    return `import { startServer } from './server';

const PORT = process.env.PORT || 3000;

startServer(Number(PORT))
  .then(() => {
    console.log(\`Server started on port \${PORT}\`);
  })
  .catch((error) => {
    console.error('Failed to start server:', error);
    process.exit(1);
  });`;
  }

  private static _generateExpressServerTs(): string {
    return `import express from 'express';
import { createServer } from 'http';
import { WebSocketServer } from 'ws';
import { TimeTravelEngine } from 'debug-time-machine-core';

const timeTravelEngine = new TimeTravelEngine();

export async function startServer(port: number): Promise<void> {
  const app = express();
  const server = createServer(app);
  const wss = new WebSocketServer({ server });

  app.use(express.json());

  // REST endpoints
  app.get('/api/health', (req, res) => {
    res.json({ status: 'ok', timestamp: new Date().toISOString() });
  });

  app.get('/api/snapshots', (req, res) => {
    const snapshots = timeTravelEngine.getAllSnapshots();
    res.json(snapshots);
  });

  // WebSocket connection
  wss.on('connection', (ws) => {
    console.log('Client connected');
    
    ws.on('message', (message) => {
      try {
        const data = JSON.parse(message.toString());
        console.log('Received:', data);
        
        // Echo back
        ws.send(JSON.stringify({
          type: 'echo',
          data,
          timestamp: Date.now(),
        }));
      } catch (error) {
        console.error('Error parsing message:', error);
      }
    });

    ws.on('close', () => {
      console.log('Client disconnected');
    });
  });

  return new Promise((resolve) => {
    server.listen(port, () => {
      resolve();
    });
  });
}`;
  }

  private static _generateNodeTsConfig(): string {
    return JSON.stringify(
      {
        compilerOptions: {
          target: 'ES2020',
          module: 'commonjs',
          lib: ['ES2020'],
          outDir: './dist',
          rootDir: './src',
          strict: true,
          esModuleInterop: true,
          skipLibCheck: true,
          forceConsistentCasingInFileNames: true,
          declaration: true,
          declarationMap: true,
          sourceMap: true,
        },
        include: ['src/**/*'],
        exclude: ['node_modules', 'dist'],
      },
      null,
      2
    );
  }
}
